import { useEffect, useCallback, useState } from 'react';
import { announce } from '../utils/a11y';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  handler: () => void;
  enabled?: boolean;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

export const useKeyboardShortcuts = ({ 
  shortcuts, 
  enabled = true 
}: UseKeyboardShortcutsOptions) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Check for help shortcut (? or Shift+/)
    if (event.key === '?' || (event.shiftKey && event.key === '/')) {
      event.preventDefault();
      setIsHelpOpen(prev => !prev);
      announce(isHelpOpen ? 'Keyboard shortcuts help closed' : 'Keyboard shortcuts help opened');
      return;
    }

    // Check for registered shortcuts
    const matchingShortcut = shortcuts.find(shortcut => {
      if (shortcut.enabled === false) return false;
      
      const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatch = shortcut.ctrl ? (event.ctrlKey || event.metaKey) : !event.ctrlKey && !event.metaKey;
      const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
      const altMatch = shortcut.alt ? event.altKey : !event.altKey;
      const metaMatch = shortcut.meta ? event.metaKey : !event.metaKey;

      return keyMatch && ctrlMatch && shiftMatch && altMatch && metaMatch;
    });

    if (matchingShortcut) {
      event.preventDefault();
      matchingShortcut.handler();
    }
  }, [shortcuts, enabled, isHelpOpen]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return { isHelpOpen, setIsHelpOpen };
};

// Keyboard Shortcuts Help Modal
interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts: KeyboardShortcut[];
}

export const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({
  isOpen,
  onClose,
  shortcuts,
}) => {
  if (!isOpen) return null;

  const formatShortcut = (shortcut: KeyboardShortcut): string => {
    const keys: string[] = [];
    if (shortcut.ctrl) keys.push('Ctrl');
    if (shortcut.shift) keys.push('Shift');
    if (shortcut.alt) keys.push('Alt');
    if (shortcut.meta) keys.push('Cmd');
    keys.push(shortcut.key.toUpperCase());
    return keys.join(' + ');
  };

  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    const category = shortcut.description.includes('Navigate') ? 'Navigation' :
                    shortcut.description.includes('Create') || shortcut.description.includes('Save') ? 'Actions' :
                    'General';
    
    if (!acc[category]) acc[category] = [];
    acc[category].push(shortcut);
    return acc;
  }, {} as Record<string, KeyboardShortcut[]>);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-title"
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-navy-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 id="shortcuts-title" className="text-2xl font-semibold text-white">
                Keyboard Shortcuts
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Close keyboard shortcuts help"
              >
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="space-y-6">
              {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
                <div key={category}>
                  <h3 className="text-lg font-medium text-professional-blue-300 mb-3">
                    {category}
                  </h3>
                  <div className="space-y-2">
                    {categoryShortcuts.map((shortcut, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      >
                        <span className="text-gray-300">{shortcut.description}</span>
                        <kbd className="px-3 py-1 text-sm font-mono bg-white/10 rounded border border-white/20 text-white">
                          {formatShortcut(shortcut)}
                        </kbd>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-sm text-gray-400 text-center">
                Press <kbd className="px-2 py-1 text-xs font-mono bg-white/10 rounded border border-white/20">?</kbd> to toggle this help
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Common keyboard shortcuts for scenario management
export const scenarioManagementShortcuts: KeyboardShortcut[] = [
  {
    key: 'n',
    ctrl: true,
    description: 'Create new scenario',
    handler: () => console.log('Create new scenario'),
  },
  {
    key: 's',
    ctrl: true,
    description: 'Save current scenario',
    handler: () => console.log('Save scenario'),
  },
  {
    key: 'f',
    ctrl: true,
    description: 'Search scenarios',
    handler: () => console.log('Search scenarios'),
  },
  {
    key: 'ArrowLeft',
    description: 'Navigate to previous scenario',
    handler: () => console.log('Previous scenario'),
  },
  {
    key: 'ArrowRight',
    description: 'Navigate to next scenario',
    handler: () => console.log('Next scenario'),
  },
  {
    key: 'Delete',
    shift: true,
    description: 'Delete selected scenario',
    handler: () => console.log('Delete scenario'),
  },
  {
    key: 'e',
    description: 'Edit selected scenario',
    handler: () => console.log('Edit scenario'),
  },
  {
    key: 'Escape',
    description: 'Close modal/dialog',
    handler: () => console.log('Close modal'),
  },
];